import {
  CustomPaging,
  DataTypeProvider,
  EditingState,
  IntegratedSelection,
  PagingState,
  SelectionState,
  Sorting,
  SortingState,
  TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  PagingPanel,
  VirtualTable,
  Table,
  TableEditRow,
  TableFixedColumns,
  TableHeaderRow,
  TableColumnResizing,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import { Button, IconButton, Paper, SvgIcon, Tooltip } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import { ReactComponent as SvgSortIcon } from 'assets/sorticon.svg';
import {
  DataTypeEditors,
  DataTypeFormatters,
} from 'components/atoms/CustomGridComponents/DataTypeProvider';
import Hide from 'components/atoms/Hide';
import Loading from 'components/atoms/Loading';
import WrapperWithTooltip from 'components/atoms/WrapperWithTooltip';
import { GROUPS } from 'constants/permissions';
import { LABEL_SITUATION } from 'constants/stockPosition';
import dayjs from 'dayjs';
import { useAuth } from 'hooks/useAuth';
import { debounce } from 'lodash';
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { putGridStockPosition } from 'services/stock-position';
import ModalAdd from './components/ModalAdd/index';
import ModalEntryHistory from './components/ModalEntryHistory';
import ModalEntryRemovalDate from './components/ModalEntryRemovalDate';
import ModalExitSST from './components/ModalExitSST';
import ModalRemovalHistory from './components/ModalRemovalHistory';
import ModalTransfer from './components/ModalTransfer';
import useStyles from './styles';
import {
  ICustomChangeSet,
  IEditedRows,
  IGrid,
  IRow,
  ISelectOption,
  IValidationRules,
} from './types';

const tableMessages = {
  noData: 'Sem dados disponíveis',
};

const pagingPanelMessages = {
  showAll: 'Todos',
  rowsPerPage: 'Linhas por página',
  info: ({ from, to, count }) => `${from}-${to} de ${count}`,
};
const StockPositionGrid = ({
  columns,
  currentPage,
  pageSize,
  pageSizes,
  totalCount,
  tooltipMaxStrLength,
  rows,
  dayMonthYearCells = [],
  entryDateCells = [],
  removalDateCells = [],
  blrCurrecyCells = [],
  quantityCells = [],
  tooltipCells = [],
  loading,
  validationRules,
  refetch,
  getRowById,
  getRowId,
  onCurrentPageChange,
  onSortingChange,
  onPageSizeChange,
  onExport,
}: IGrid) => {
  const classes = useStyles();
  const { checkPermissions } = useAuth();

  const gridRef = useRef<HTMLElement>();
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const hiddenScrollDivRef = useRef<HTMLDivElement>(null);

  const [modalAdd, setModalAdd] = React.useState<boolean>(false);
  const [modalTransfer, setModalTransfer] = React.useState<boolean>(false);
  const [modalEntryRemovalDate, setModalEntryRemovalDate] =
    React.useState<boolean>(false);
  const [modalExitSST, setModalExitSST] = React.useState<boolean>(false);

  const [selection, setSelection] = React.useState<(string | number)[]>([]);
  const [editedRows, setEditedRows] = React.useState<IEditedRows[]>([]);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<IValidationRules>({});
  const [editingRowIds, setEditingRowIds] = React.useState<(string | number)[]>(
    [],
  );
  const [rowChanges, setRowChanges] = React.useState({});
  const [updatingGridData, setUpdatingGridData] =
    React.useState<boolean>(false);

  const canSave = React.useMemo(() => {
    // se esta editando E NÃO se existe algum erro em alguma linha
    return (
      isEditing &&
      !Object.values(errors).some(rowId =>
        Object.values(rowId).some(row => !row.isValid),
      )
    );
  }, [errors, isEditing]);

  const isPlanningOrEngineeringUser = React.useMemo(
    () =>
      checkPermissions([
        GROUPS.ADMINISTRATOR,
        GROUPS.PLANNING,
        GROUPS.ENGINEERING,
      ]),
    [checkPermissions],
  );

  const isTechnicianUser = React.useMemo(
    () => checkPermissions([GROUPS.ADMINISTRATOR, GROUPS.TECHNICIAN]),
    [checkPermissions],
  );

  const [columnWidths, setColumnWidths] = React.useState<
    TableColumnWidthInfo[]
  >(
    columns.map(column => {
      return {
        columnName: column.name,
        width: column.width,
      } as TableColumnWidthInfo;
    }),
  );

  const columnWidthsMemo = useMemo<TableColumnWidthInfo[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          width: column.width,
          wordWrapEnabled: true,
          align: column.align,
        } as TableColumnWidthInfo;
      }),
    [columns],
  );

  const updateColumnWidth = React.useCallback(colsWidth => {
    setColumnWidths(colsWidth);
  }, []);

  const sorting = React.useMemo<Sorting[]>(
    () =>
      columns.map(column => {
        return {
          columnName: column.name,
          direction: column.direction,
        } as Sorting;
      }),
    [columns],
  );

  const editingStateColumnExtensions = React.useMemo(() => {
    if (isPlanningOrEngineeringUser) {
      return columns.map(column => {
        return {
          columnName: column.name,
          editingEnabled: Boolean(column?.editingEnabled),
        };
      });
    }
    if (isTechnicianUser) {
      return columns.map(column => {
        return {
          columnName: column.name,
          editingEnabled:
            Boolean(column?.editingEnabled) &&
            column.name === 'currentQuantity',
        };
      });
    }

    return columns.map(column => {
      return {
        columnName: column.name,
        editingEnabled: false,
      };
    });
  }, [columns, isPlanningOrEngineeringUser, isTechnicianUser]);

  const disabledSelectionButtons = React.useMemo(() => {
    return selection?.length !== 1;
  }, [selection]);

  const getFirstSelectedRow = React.useMemo(() => {
    if (disabledSelectionButtons) return null;

    const rowId = selection[0];
    const row = getRowById(rowId);

    return row || null;
  }, [disabledSelectionButtons, getRowById, selection]);

  const getSelectedRows = React.useMemo(() => {
    return selection.map(rowId => {
      return getRowById(rowId) as IRow;
    });
  }, [getRowById, selection]);

  const disableCancelButton = React.useMemo(() => {
    return !isEditing && selection?.length === 0;
  }, [isEditing, selection?.length]);

  const cantExitSST = React.useMemo(() => {
    return getSelectedRows.some(row => {
      if (row?.materialClass !== 'Saude e Seguranca') {
        return true;
      }

      return Boolean(row?.currentQuantity) && row?.currentQuantity <= 0;
    });
  }, [getSelectedRows]);

  const disabledSelectionButtonExitSST = React.useMemo(() => {
    if (disabledSelectionButtons) return disabledSelectionButtons;

    return cantExitSST;
  }, [cantExitSST, disabledSelectionButtons]);

  const disabledSelectionButtonTransfer = React.useMemo(() => {
    // a linha só é habilitada quando tem uma apenas 1 linha selecionada OU
    // alguma linha possui a quantidade atual diferente de zero
    return (
      selection?.length !== 1 ||
      getSelectedRows.some(
        row => Boolean(row?.currentQuantity) && row?.currentQuantity <= 0,
      )
    );
  }, [getSelectedRows, selection?.length]);

  const disableEditingButton = React.useMemo(() => {
    // caso a linha selecionada esteja em uso,
    // quantidade atual for igual a 1 e possua proprietário
    // a linha deve ser bloqueada para edição
    const isInUseItem = getSelectedRows.some(
      row =>
        row?.situation === 'Em uso' &&
        Number(row?.currentQuantity) === 1 &&
        Boolean(row?.owner),
    );

    // a linha só é habilitada quando tem 1  ou mais linha(s) ou possui algum item em uso
    const baseCondition = selection?.length === 0 || isInUseItem;

    // se o usuário é do grupo planejamento ou engenharia ele só vai ter a restrição da seleção
    if (isPlanningOrEngineeringUser) return baseCondition;

    if (isTechnicianUser) {
      // caso não seja, ele estará sujeito a seguinte condição
      return (
        baseCondition ||
        // TODO: alterar para id de saúde e segurança
        // caso o usuário seja do grupo técnico e exista algum
        // item com a classe Saúde e Segurança o botão deverá ficar desabilitado
        getSelectedRows.some(row => row?.materialClass === 'Saude e Seguranca')
      );
    }

    return false;
  }, [
    selection?.length,
    isPlanningOrEngineeringUser,
    isTechnicianUser,
    getSelectedRows,
  ]);

  const sortColumnsData = React.useMemo(() => {
    const columnsData = {};

    columns.forEach(({ name, sortingEnabled }) => {
      columnsData[name] = sortingEnabled;
    });

    return columnsData;
  }, [columns]);

  const renderTitleComponent = React.useCallback(
    props => {
      const { children } = props;
      const columnData = columns.find(column => column.title === children);

      if (columnData?.fullTitle) {
        const renderTitle = () => (
          <span className={classes.tooltipLabel}>{columnData?.fullTitle}</span>
        );

        return (
          <TableHeaderRow.Title className={classes.tableTitle} {...props}>
            <Tooltip title={renderTitle()}>
              <span>{children}</span>
            </Tooltip>
          </TableHeaderRow.Title>
        );
      }

      return <TableHeaderRow.Title className={classes.tableTitle} {...props} />;
    },
    [classes.tableTitle, classes.tooltipLabel, columns],
  );

  const renderTableSelectionRowComponent = React.useCallback(
    props => {
      const { highlighted } = props;

      return (
        <Table.Row
          {...props}
          className={highlighted ? classes.selectedRows : null}
        />
      );
    },
    [classes.selectedRows],
  );

  const TooltipFormatter = React.useCallback(
    ({ value }: { value: string }) => {
      const maxLength = tooltipMaxStrLength || 50;

      if (value && value.toString().length > maxLength) {
        const trucatedValue = `${value.toString().substr(0, 50)}...`;

        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={<span className={classes.tooltipLabel}>{value}</span>}
          >
            <span>{trucatedValue}</span>
          </Tooltip>
        );
      }

      return value;
    },
    [classes.tooltipCenterContent, classes.tooltipLabel, tooltipMaxStrLength],
  );

  const CellTooltip = React.useCallback(
    props => (
      <DataTypeProvider
        for={tooltipCells}
        formatterComponent={TooltipFormatter}
        editorComponent={TooltipFormatter}
        {...props}
      />
    ),
    [TooltipFormatter, tooltipCells],
  );

  const CellEntryDateCustom = React.useCallback(({ value, row }) => {
    return (
      <>
        <ModalEntryHistory gridRow={row} history={value}>
          {DataTypeFormatters.DateCellFormatter({
            value: dayjs(value).toString(),
            format: 'DD/MM/YY',
          })}
        </ModalEntryHistory>
      </>
    );
  }, []);

  const CellEntryDate = React.useCallback(
    props => (
      <DataTypeProvider
        for={entryDateCells}
        formatterComponent={CellEntryDateCustom}
        editorComponent={CellEntryDateCustom}
        {...props}
      />
    ),
    [CellEntryDateCustom, entryDateCells],
  );

  const CellRemovalDateCustom = React.useCallback(({ value, row }) => {
    return (
      <>
        <ModalRemovalHistory gridRow={row} history={value}>
          {DataTypeFormatters.DateCellFormatter({
            value: dayjs(value).toString(),
            format: 'DD/MM/YY',
          })}
        </ModalRemovalHistory>
      </>
    );
  }, []);

  const CellRemovalDate = React.useCallback(
    props => (
      <DataTypeProvider
        for={removalDateCells}
        formatterComponent={CellRemovalDateCustom}
        editorComponent={CellRemovalDateCustom}
        {...props}
      />
    ),
    [CellRemovalDateCustom, removalDateCells],
  );

  const CellDayMonthYear = React.useCallback(
    props => (
      <DataTypeProvider
        for={dayMonthYearCells}
        formatterComponent={({ value }) => {
          return DataTypeFormatters.DateCellFormatter({
            value: dayjs(value).toString(),
            format: 'DD/MM/YY',
          });
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled)
            return DataTypeFormatters.DateCellFormatter({
              value: dayjs(value).toString(),
              format: 'DD/MM/YY',
            });

          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.DateEditorComponent({
            format: 'DD/MM/YY',
            error: hasError,
            value,
            onValueChange,
            placeholder: '',
          });
        }}
        {...props}
      />
    ),
    [dayMonthYearCells, errors, getRowId],
  );

  const CellQuantity = React.useCallback(
    props => {
      const formatter = ({ value }) => Number(value);
      return (
        <DataTypeProvider
          for={quantityCells}
          formatterComponent={formatter}
          editorComponent={({
            column,
            row,
            value,
            onValueChange,
            disabled,
          }) => {
            if (disabled) {
              return formatter({ value });
            }

            const rowId = getRowId(row);
            const hasError =
              Boolean(errors[rowId]?.[column.name]) &&
              !errors[rowId]?.[column.name]?.isValid;

            return (
              <DataTypeEditors.InputNumberEditorComponent
                value={Number(value)}
                error={hasError}
                onValueChange={onValueChange}
              />
            );
          }}
          {...props}
        />
      );
    },
    [errors, getRowId, quantityCells],
  );

  const CellBlrCurrency = React.useCallback(
    props => {
      const formatter = ({ value }) =>
        Number(value).toLocaleString('pt-br', {
          minimumFractionDigits: 2,
        });

      return (
        <DataTypeProvider
          for={blrCurrecyCells}
          formatterComponent={formatter}
          {...props}
        />
      );
    },
    [blrCurrecyCells],
  );

  const CellOracleCode = React.useCallback(
    props => (
      <DataTypeProvider
        for={['oracleCode']}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;
          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.InputTextEditorComponent({
            error: hasError,
            value,
            onValueChange,
            maxLength: 45,
          });
        }}
        {...props}
      />
    ),
    [errors, getRowId],
  );

  const SortingIcon = React.useCallback(
    () => <SvgIcon component={SvgSortIcon} />,
    [],
  );
  const renderSortLabel = React.useCallback(
    ({ children, onSort, column }) => {
      return (
        <>
          {children}
          {sortColumnsData[column.name] && (
            <IconButton
              className={classes.iconButtonContainer}
              onClick={onSort}
            >
              <SortingIcon />
            </IconButton>
          )}
        </>
      );
    },
    [SortingIcon, classes.iconButtonContainer, sortColumnsData],
  );

  const validate = React.useCallback(
    (_editedRows: { [key: string]: IRow }): IValidationRules => {
      return Object.entries(_editedRows).reduce((acc, [rowId, partialRow]) => {
        const validatedFields = {};
        Object.entries(partialRow).forEach(([colName, colValue]) => {
          if (validationRules && validationRules[colName]) {
            const row = {
              ...(getRowById(rowId) || ({} as IRow)),
              ...partialRow,
            };
            validatedFields[colName] = {
              isValid: validationRules[colName].isValid(colValue, row),
              error: validationRules[colName].errorText,
            };
          }
        });

        return {
          ...acc,
          [rowId]: validatedFields,
        };
      }, {});
    },
    [getRowById, validationRules],
  );

  const onEdited = debounce(edited => {
    const rowsValidated = validate(edited);

    setErrors(rowsValidated);
  }, 250);

  const onSave = React.useCallback(
    // TODO: add typagem
    edited => {
      return new Promise<null>((resolve, reject) => {
        putGridStockPosition({ stocksToUpdate: edited })
          .then(() => {
            if (refetch) refetch();
            resolve(null);
            setEditedRows([]);
          })
          .catch(e => reject(e));
      });
    },
    [refetch],
  );

  const commitChanges = React.useCallback(
    ({ changed }: ICustomChangeSet) => {
      const edited = Object.entries(changed || {}).map(([rowId, rowValues]) => {
        const onlyEditableColumns = editingStateColumnExtensions
          .filter(column => column.editingEnabled)
          .map(column => column.columnName);

        const filtered: IRow = Object.fromEntries(
          Object.entries(rowValues).filter(([key]) =>
            onlyEditableColumns.includes(key),
          ),
        );

        const editedRow = getRowById(rowId);

        const oldQuantity = Number(editedRow?.currentQuantity) || 0;
        const currentQuantity =
          typeof filtered?.currentQuantity === 'number'
            ? Number(filtered?.currentQuantity)
            : Number(editedRow?.currentQuantity);
        const movementQuantity: number = currentQuantity - oldQuantity;

        return {
          stockPositionId: Number(rowId),
          ...filtered,
          movementDate: null,
          movementQuantity,
          itemCode: getRowById(rowId)?.itemCode,
          modifiedField: Object.keys(filtered),
        };
      });

      const isNecessaryOpenModal = edited.some(
        row => row.movementQuantity !== 0,
      );

      if (isNecessaryOpenModal) {
        setEditedRows(edited as IEditedRows[]);
        setModalEntryRemovalDate(true);
      } else {
        setUpdatingGridData(true);
        onSave(edited).finally(() => {
          setUpdatingGridData(false);
        });
      }
    },
    [editingStateColumnExtensions, getRowById, onSave],
  );

  const cancelEditingAction = React.useCallback(() => {
    setEditingRowIds([]);
    setIsEditing(false);
    setSelection([]);
    setRowChanges([]);
  }, []);

  const editingSelectedColumns = React.useCallback(() => {
    if (isEditing) {
      if (Object.keys(rowChanges).length > 0) {
        commitChanges({ changed: rowChanges });
      }
      setEditingRowIds([]);
      setIsEditing(false);
      setSelection([]);
    } else {
      setEditingRowIds(selection);
      setIsEditing(true);
    }
  }, [isEditing, rowChanges, commitChanges, selection]);

  const RowEditingComponent = React.useCallback(
    props => {
      return <Table.Row {...props} className={classes.selectedRows} />;
    },
    [classes.selectedRows],
  );

  const CellSituation = React.useCallback(
    props => (
      <DataTypeProvider
        for={['situation']}
        formatterComponent={({ value }) => {
          return value;
        }}
        editorComponent={({ column, row, value, onValueChange, disabled }) => {
          if (disabled) return value;

          const rowId = getRowId(row);
          const hasError =
            Boolean(errors[rowId]?.[column.name]) &&
            !errors[rowId]?.[column.name]?.isValid;

          return DataTypeEditors.SelectOptionsEditorComponent({
            options: Object.values(LABEL_SITUATION).map(item => ({
              label: item,
              value: item,
            })) as ISelectOption[],
            isLoading: false,
            error: hasError,
            value,
            onValueChange,
          });
        }}
        {...props}
      />
    ),
    [errors, getRowId],
  );

  const CellEditingComponent = React.useCallback(
    props => {
      const {
        tableRow: { rowId },
        column: { name: columnName },
      } = props;
      const columnStatus = errors[rowId]?.[columnName];
      const valid = !columnStatus || columnStatus.isValid;
      const title = valid ? '' : errors[rowId][columnName].error;

      return <Table.Cell {...props} title={title} />;
    },
    [errors],
  );

  React.useEffect(() => {
    // obtendo todos os ids das linhas da grid
    const rowIds = rows.map(row => getRowId(row));

    /**
     * Atualizo a lista de seleções para corresponder
     * apenas as linhas que estão sendo exibidas.
     */
    setSelection(currentValues => {
      return currentValues.filter(item => rowIds.includes(item));
    });
  }, [getRowId, rows]);

  useLayoutEffect(() => {
    const rootBase = gridRef?.current?.firstChild; // Obtem o elemento RootBase da Grid
    const tableRootContainer = rootBase?.firstChild as HTMLElement; // Container da Tabela para adicionar o evento de scroll horizontal
    const tableOuterDiv = tableRootContainer?.firstChild; // O componente gera uma div pura entre a tabela e o seu container
    const table = tableOuterDiv?.firstChild as HTMLElement; // Elemento da tabela para obter a sua largura

    const hiddenScrollDiv = hiddenScrollDivRef.current as HTMLElement; // Elemento dentro da div externa que possuirá o mesmo tamanho da tabela para scroll externo
    hiddenScrollDiv.style.width = `${table?.offsetWidth + 85}px`; // Atribui a largura da tabela + 85px da Coluna de Checkbox

    const scrollDiv = scrollDivRef.current as HTMLElement; // Elemento onde será colocado o scroll externo

    // Função que será executada ao rolar o scroll externo
    const scrollListener = ({ target }) => {
      const currentScroll = target as HTMLElement;
      tableRootContainer.scrollLeft = currentScroll.scrollLeft; // Scroll da tabela reflete a posição do scroll externo
    };
    scrollDiv.removeEventListener('scroll', scrollListener); // Quando a largura do grid mudar, remover o listener antes de adicionar um novo
    scrollDiv.addEventListener('scroll', scrollListener); // Adicionar o listener para o scroll externo

    return () => scrollDiv.removeEventListener('scroll', scrollListener); // Remover listener ao desmontar o componente
  }, [columnWidthsMemo]); // Observar a mudança da largura do grid

  return (
    <div className={classes.root}>
      <div className={classes.summary}>
        <div className={classes.buttonsActionsContainer}>
          <div className={classes.buttonsActionsContainerRight}>
            <ModalAdd
              open={modalAdd}
              onClose={() => setModalAdd(false)}
              reloadData={refetch}
            >
              <Button onClick={() => setModalAdd(true)} color="primary">
                Adicionar
              </Button>
            </ModalAdd>
            <ModalExitSST
              open={modalExitSST}
              onClose={() => setModalExitSST(false)}
              gridRow={getFirstSelectedRow}
              reloadData={refetch}
            >
              <WrapperWithTooltip
                condition={cantExitSST}
                tooltipMessage="Não é possível utilizar Saída SST"
              >
                <Button
                  onClick={() => setModalExitSST(true)}
                  color="secondary"
                  disabled={disabledSelectionButtonExitSST}
                >
                  Saída SST
                </Button>
              </WrapperWithTooltip>
            </ModalExitSST>
            <ModalTransfer
              open={modalTransfer}
              onClose={() => setModalTransfer(false)}
              gridRow={getFirstSelectedRow}
              reloadData={refetch}
            >
              <Button
                onClick={() => setModalTransfer(true)}
                color="primary"
                disabled={disabledSelectionButtonTransfer}
              >
                Transferir
              </Button>
            </ModalTransfer>
            {/* INÍCIO DIVISÃO DA AÇÃO DE EDITAR */}
            <Hide
              hide={
                !checkPermissions([
                  GROUPS.ADMINISTRATOR,
                  GROUPS.PLANNING,
                  GROUPS.ENGINEERING,
                  GROUPS.TECHNICIAN,
                ]) || isEditing
              }
            >
              <Button
                disabled={disableEditingButton}
                onClick={editingSelectedColumns}
                color="primary"
              >
                {isPlanningOrEngineeringUser ? 'Editar' : 'Entrada/Saída'}
              </Button>
            </Hide>
            <ModalEntryRemovalDate
              open={modalEntryRemovalDate}
              onClose={() => {
                setModalEntryRemovalDate(false);
                cancelEditingAction();
              }}
              editedRows={editedRows}
              onSave={editedToSave => {
                return onSave(editedToSave);
              }}
            >
              <Hide
                hide={
                  !checkPermissions([
                    GROUPS.ADMINISTRATOR,
                    GROUPS.PLANNING,
                    GROUPS.ENGINEERING,
                    GROUPS.TECHNICIAN,
                  ]) || !isEditing
                }
              >
                <WrapperWithTooltip
                  condition={!canSave}
                  tooltipMessage="Existem campos inválidos na grid"
                >
                  <Button
                    disabled={!canSave}
                    onClick={editingSelectedColumns}
                    color="primary"
                  >
                    Salvar
                  </Button>
                </WrapperWithTooltip>
              </Hide>
            </ModalEntryRemovalDate>
            <Hide
              hide={
                !checkPermissions([
                  GROUPS.ADMINISTRATOR,
                  GROUPS.ENGINEERING,
                  GROUPS.PLANNING,
                  GROUPS.TECHNICIAN,
                ]) || !isEditing
              }
            >
              <Button
                onClick={cancelEditingAction}
                disabled={disableCancelButton}
                color="primary"
              >
                Cancelar
              </Button>
            </Hide>
            {/* FIM DIVISÃO DA AÇÃO DE EDITAR */}
            <IconButton onClick={onExport}>
              <Tooltip
                title={
                  <span className={classes.tooltipLabel}>Exportar dados</span>
                }
              >
                <GetAppIcon color="primary" />
              </Tooltip>
            </IconButton>
          </div>
        </div>
      </div>
      <Paper
        ref={gridRef}
        variant="elevation"
        elevation={0}
        className={classes.container}
      >
        <Grid getRowId={getRowId} columns={columns} rows={rows}>
          <SelectionState
            selection={selection}
            onSelectionChange={setSelection}
          />
          <SortingState sorting={sorting} onSortingChange={onSortingChange} />
          <IntegratedSelection />
          <CellDayMonthYear />
          <CellEntryDate />
          <CellRemovalDate />
          <CellQuantity />
          <CellBlrCurrency />
          <CellOracleCode />
          <CellSituation />
          <EditingState
            editingRowIds={editingRowIds}
            rowChanges={rowChanges}
            onRowChangesChange={newChangedRows => {
              onEdited(newChangedRows);
              setRowChanges(newChangedRows);
            }}
            onCommitChanges={commitChanges}
            columnExtensions={editingStateColumnExtensions}
          />
          <PagingState
            currentPage={currentPage}
            onCurrentPageChange={onCurrentPageChange}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
          <CustomPaging totalCount={totalCount} />
          <CellTooltip />
          <VirtualTable
            messages={tableMessages}
            columnExtensions={columnWidthsMemo}
            cellComponent={props => {
              const { column, row } = props;
              const cellValue = row[column.name];
              if (cellValue) return <Table.Cell {...props} />;
              return <Table.Cell {...props}> - </Table.Cell>;
            }}
          />

          <TableColumnResizing
            columnWidths={columnWidths}
            onColumnWidthsChange={updateColumnWidth}
          />

          <TableHeaderRow
            titleComponent={renderTitleComponent}
            sortLabelComponent={renderSortLabel}
            showSortingControls
          />
          <TableSelection
            rowComponent={renderTableSelectionRowComponent}
            highlightRow
            showSelectAll
          />

          <TableEditRow
            rowComponent={RowEditingComponent}
            cellComponent={CellEditingComponent}
          />

          <TableFixedColumns
            cellComponent={props => (
              <TableFixedColumns.Cell {...props} showRightDivider={false} />
            )}
            leftColumns={[TableSelection.COLUMN_TYPE]}
          />
          <PagingPanel messages={pagingPanelMessages} pageSizes={pageSizes} />
        </Grid>
        <div ref={scrollDivRef} className={classes.scrollDiv}>
          <div ref={hiddenScrollDivRef} className={classes.hiddenScrollDiv} />
        </div>
        {(loading || updatingGridData) && <Loading />}
      </Paper>
    </div>
  );
};

export default StockPositionGrid;
